// File: src/components/InfiniteLoader.jsx
// Reusable infinite loader that supports:
// - Auto-load on scroll (IntersectionObserver)
// - Optional "Cargar más" button (fallback, accessibility, SEO control)
// - Pluggable renderer (Card) and pluggable data source (endpoint / fetcher)
// - Works in plain React or Next.js (SSR prefetch for first page is optional)
// - JavaScript with JSDoc types; easy to migrate to TS

import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import { ProductSkeletonCard } from "../ProductCardBase/ProductSkeletonCard";
import { Grid } from "@material-ui/core";
import Button from "components/CustomButtons/Button.jsx";

/**
 * @typedef {Object} PageResult
 * @property {any[]} items - Array of items for this page
 * @property {boolean} hasMore - Whether there are more pages
 * @property {string|number|undefined} [nextCursor] - Optional cursor for the next page
 * @property {number|undefined} [page] - Page number returned by the server (1-based or 0-based—your choice)
 * @property {number|undefined} [total] - Optional total items count (if your API returns it)
 */

/**
 * @callback FetchPage
 * @param {{ page: number, pageSize: number, cursor?: string|number, signal?: AbortSignal }} params
 * @returns {Promise<PageResult>}
 */

/**
 * @callback RenderItem
 * @param {any} item - Item to render
 * @param {number} index - Index within the *aggregated* list
 * @returns {React.ReactNode}
 */

/**
 * @callback GetItemKey
 * @param {any} item
 * @param {number} index
 * @returns {React.Key}
 */

/**
 * @typedef {Object} InfiniteLoaderProps
 * @property {FetchPage} fetchPage - Function that fetches a page from any endpoint
 * @property {RenderItem} renderItem - How to render each item (Card component, row, etc.)
 * @property {GetItemKey} [getItemKey] - Key generator per item (defaults to index)
 * @property {number} [pageSize] - Page size for the fetcher (default 20)
 * @property {boolean} [autoLoad] - Auto load more pages on scroll (default true)
 * @property {number} [autoLoadUntil] - Number of auto-loads before switching to button (default 3)
 * @property {boolean} [showLoadMoreButton] - Show the manual button (default true)
 * @property {React.ReactNode} [emptyComponent] - Render when there is no data
 * @property {React.ReactNode} [loadingComponent] - Custom loading UI (for first load and next loads)
 * @property {React.ReactNode} [errorComponent] - Optional error UI (if omitted, a default minimal one is shown)
 * @property {(items: any[]) => void} [onItemsChange] - Callback when aggregated items change
 * @property {string} [className] - Optional container className
 * @property {number} [rootMarginPx] - Pixels to start loading before reaching the bottom (default 200)
 */

export const InfiniteLoader = forwardRef(function InfiniteLoader(
	{
		fetchPage,
		renderItem,
		getItemKey,
		pageSize = 20,
		autoLoad = true,
		autoLoadUntil = 3,
		showLoadMoreButton = true,
		emptyComponent = null,
		loadingComponent = null,
		errorComponent = null,
		onItemsChange,
		className,
		rootMarginPx = 200,
	},
	ref
) {
	const [items, setItems] = useState([]);
	const [page, setPage] = useState(1);
	const [cursor, setCursor] = useState(undefined);
	const [hasMore, setHasMore] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [isFirstLoad, setIsFirstLoad] = useState(true);
	const [error, setError] = useState(null);
	const [autoLoadsDone, setAutoLoadsDone] = useState(0);

	const abortRef = useRef(/** @type {AbortController | null} */ (null));
	const sentinelRef = useRef(null);

	// Evitar cargas concurrentes y doble efecto en StrictMode
	const inFlightRef = useRef(false);
	const didInitRef = useRef(false);
	const [resetTrigger, setResetTrigger] = useState(0);

	const onItemsChangeStable = useMemo(
		() => (typeof onItemsChange === "function" ? onItemsChange : function() {}),
		[onItemsChange]
	);

	// Ref para almacenar la función load actual (para uso en reset)
	const loadRef = useRef(
		/** @type {((mode?: string) => Promise<void>) | null} */ (null)
	);

	const load = useCallback(
		async function(mode) {
			if (mode === void 0) mode = "auto";
			// Prevenir carga automática si hay error (solo permitir retry manual)
			if (mode === "auto" && error) return;

			// Para retry manual después de error, restaurar hasMore y permitir el retry
			const isManualRetry = mode === "manual" && error;
			if (isManualRetry && !hasMore) {
				setHasMore(true);
			}

			// Solo verificar hasMore si no es un retry manual después de error
			if (!isManualRetry && (isLoading || inFlightRef.current || !hasMore))
				return;
			if (isManualRetry && (isLoading || inFlightRef.current)) return;

			inFlightRef.current = true;
			setIsLoading(true);
			setError(null);

			if (abortRef.current && typeof abortRef.current.abort === "function") {
				abortRef.current.abort();
			}

			const controller = new AbortController();
			abortRef.current = controller;

			try {
				const res = await fetchPage({
					page: page,
					pageSize: pageSize,
					cursor: cursor,
					signal: controller.signal,
				});

				console.log(res);

				const newItems = res && res.items ? res.items : [];
				const merged = items.concat(newItems);
				setItems(merged);
				onItemsChangeStable(merged);

				setHasMore(Boolean(res && res.hasMore));
				setCursor(res && res.nextCursor ? res.nextCursor : undefined);

				setPage(function(p) {
					return typeof (res && res.page) === "number" ? res.page + 1 : p + 1;
				});

				if (mode === "auto") {
					setAutoLoadsDone(function(n) {
						return n + 1;
					});
				}
			} catch (err) {
				if (!(err && err.name === "AbortError")) {
					setError(err);
					// Detener el auto-load cuando hay error para evitar ciclos
					setHasMore(false);
				}
			} finally {
				inFlightRef.current = false;
				setIsLoading(false);
				setIsFirstLoad(false);
			}
		},
		[
			cursor,
			fetchPage,
			hasMore,
			isLoading,
			items,
			onItemsChangeStable,
			page,
			pageSize,
			error, // Agregar error como dependencia
		]
	);

	// Actualizar el ref cada vez que load cambie
	useEffect(() => {
		loadRef.current = load;
	}, [load]);

	// Exponer método reset() mediante useImperativeHandle
	useImperativeHandle(
		ref,
		() => ({
			reset: () => {
				// Cancelar cualquier petición en curso
				if (abortRef.current && typeof abortRef.current.abort === "function") {
					abortRef.current.abort();
				}

				// Resetear todos los estados a sus valores iniciales
				setItems([]);
				setPage(1);
				setCursor(undefined);
				setHasMore(true);
				setIsLoading(false);
				setIsFirstLoad(true);
				setError(null);
				setAutoLoadsDone(0);

				// Resetear refs internos
				inFlightRef.current = false;
				didInitRef.current = false;

				// Limpiar abortRef
				abortRef.current = null;

				// Disparar re-render para que el efecto de reset se ejecute
				setResetTrigger((prev) => prev + 1);
			},
		}),
		[] // Sin dependencias - el ref se actualiza automáticamente
	);

	// First load (protegido vs StrictMode)
	useEffect(function() {
		if (didInitRef.current) return;
		didInitRef.current = true;
		load("auto");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Efecto para manejar reset externo
	useEffect(
		function() {
			// Solo ejecutar si resetTrigger > 0 (se hizo un reset)
			if (resetTrigger === 0) return;

			// Resetear didInitRef para permitir nueva carga inicial
			didInitRef.current = false;

			// Disparar la primera carga después de que los estados se hayan actualizado
			// Usamos setTimeout para asegurar que todos los setters de estado se hayan procesado
			const timeoutId = setTimeout(() => {
				didInitRef.current = true;
				load("auto");
			}, 0);

			return () => {
				clearTimeout(timeoutId);
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[resetTrigger, load]
	);

	// IntersectionObserver for auto-load
	useEffect(
		function() {
			// No activar el observer si hay error o no hay más páginas
			if (!autoLoad || !hasMore || error) return;
			if (autoLoadsDone >= autoLoadUntil) return; // switch to manual button after N auto loads
			const el = sentinelRef.current;
			if (!el) return;

			const obs = new IntersectionObserver(
				function(entries) {
					for (var i = 0; i < entries.length; i++) {
						var e = entries[i];
						if (e.isIntersecting) {
							// Guardia extra para evitar disparos múltiples (incluye verificación de error)
							if (!isLoading && !inFlightRef.current && hasMore && !error) {
								load("auto");
							}
						}
					}
				},
				{
					rootMargin:
						String(rootMarginPx) + "px 0px " + String(rootMarginPx) + "px 0px",
				}
			);

			obs.observe(el);
			return function() {
				obs.disconnect();
			};
		},
		[
			autoLoad,
			autoLoadsDone,
			autoLoadUntil,
			hasMore,
			isLoading,
			load,
			rootMarginPx,
			error, // Agregar error como dependencia
		]
	);
	const defaultLoading = (
		<Grid container spacing={2} justify="center">
			{Array.from({ length: pageSize }).map((_, i) => (
				<Grid item key={i} lg={4} md={6} sm={6} xs={12}>
					<ProductSkeletonCard />
				</Grid>
			))}
		</Grid>
	);

	const defaultError = (
		<div style={{ padding: 12, textAlign: "center" }}>
			<div style={{ marginBottom: 8 }}>
				Ocurrió un error al cargar.
				{error && error.message && (
					<div style={{ fontSize: "0.9em", color: "#666", marginTop: 4 }}>
						{error.message}
					</div>
				)}
			</div>
			<button
				onClick={function() {
					setError(null); // Limpiar error antes de reintentar
					load("manual");
				}}
				disabled={isLoading}
			>
				{isLoading ? "Reintentando…" : "Reintentar"}
			</button>
		</div>
	);

	return (
		<div className={className} style={{ display: "contents" }}>
			{/* Items */}
			{items.length === 0 &&
				isFirstLoad &&
				(loadingComponent || defaultLoading)}
			{items.length === 0 &&
				!isFirstLoad &&
				!isLoading &&
				!error &&
				emptyComponent}

			{items.map(function(item, idx) {
				return (
					<React.Fragment key={getItemKey ? getItemKey(item, idx) : idx}>
						{renderItem(item, idx)}
					</React.Fragment>
				);
			})}

			{/* Load states at bottom */}
			{error && (errorComponent || defaultError)}
			{isLoading && items.length > 0 && (loadingComponent || defaultLoading)}

			{/* Auto-load sentinel */}
			{hasMore && autoLoad && autoLoadsDone < autoLoadUntil && !error && (
				<div ref={sentinelRef} style={{ height: 1 }} />
			)}

			{/* Manual Load More */}
			{hasMore && showLoadMoreButton && (
				<div
					style={{
						width: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						padding: 16,
						gridColumn: "1 / -1", // 👈 fuerza a ocupar toda la fila del grid
					}}
				>
					<Button
						onClick={() => load("manual")}
						disabled={isLoading}
						aria-label="Cargar más resultados"
						color="primary"
						variant="contained"
						size="large"
						fullWidth
						style={{
							maxWidth: 200, // 👈 limita el ancho total
							textAlign: "center",
							textTransform: "none",
						}}
					>
						{isLoading ? "Cargando…" : "Cargar más"}
					</Button>
				</div>
			)}
		</div>
	);
});

// ---------------------------------------------------------------------------
// Example: Generic fetcher helpers for different endpoints
// ---------------------------------------------------------------------------

/**
 * Creates a page fetcher for cursor-based APIs.
 * Example GET /api/news?cursor=abc&limit=20 → { items, nextCursor }
 */
export function makeCursorFetcher(urlBase, extraQuery = {}) {
	/** @type {FetchPage} */
	return async function({ page, pageSize, cursor, signal }) {
		const params = new URLSearchParams({
			...extraQuery,
			limit: String(pageSize),
		});
		if (cursor != null) params.set("cursor", String(cursor));
		const res = await fetch(urlBase + "?" + params.toString(), {
			signal: signal,
		});
		if (!res.ok) throw new Error("HTTP " + res.status);
		const data = await res.json();
		return {
			items: data.items || data.data || [],
			hasMore: Boolean(data.nextCursor),
			nextCursor: data.nextCursor,
			page: page, // we just pass-through the internal page counter
			total: data.total,
		};
	};
}

// ---------------------------------------------------------------------------
// Usage examples (Products list vs. Another list with a different Card + endpoint)
// ---------------------------------------------------------------------------

// Example ProductCard component
export function ProductCard({ product }) {
	return (
		<article
			style={{
				border: "1px solid #eee",
				marginBottom: 12,
				padding: 12,
				borderRadius: 8,
			}}
		>
			<h3 style={{ margin: 0 }}>{product.name}</h3>
			<p style={{ margin: "8px 0" }}>{product.description}</p>
			<strong>${product.price}</strong>
		</article>
	);
}

// Example OtherCard (totally different shape)
export function ArticleCard({ article }) {
	return (
		<article
			style={{ borderLeft: "4px solid #888", padding: 12, marginBottom: 12 }}
		>
			<h3 style={{ margin: 0 }}>{article.title}</h3>
			<small>{new Date(article.publishedAt).toLocaleString()}</small>
			<p>{article.summary}</p>
		</article>
	);
}

// Example: Articles list using cursor-based API (different card + different endpoint)
export function ArticlesList() {
	const fetchArticles = useMemo(function() {
		return makeCursorFetcher("/api/articles", { category: "tech" });
	}, []);

	return (
		<InfiniteLoader
			fetchPage={fetchArticles}
			renderItem={function(item) {
				return <ArticleCard article={item} />;
			}}
			getItemKey={function(item) {
				return item.slug;
			}}
			pageSize={10}
			autoLoad
			autoLoadUntil={2}
			showLoadMoreButton
			emptyComponent={<h3>No se encontraron artículos.</h3>}
		/>
	);
}

// ---------------------------------------------------------------------------
// Notes for SEO & Next.js (optional but recommended):
// ---------------------------------------------------------------------------
// 1) Asegura rutas paginadas accesibles / crawlables (e.g., /productos?page=2). Puedes usar este
//    mismo componente en el cliente, pero expón enlaces <a href="?page=2"> para SEO (paginación clásica)
//    y añade rel="next"/"prev" cuando aplique.
// 2) Prefetch del primer page en SSR (getServerSideProps o getStaticProps) y pásalo como initialItems
//    si deseas (puedes extender este componente para aceptar initialItems y initialPageInfo).
// 3) Mantén el botón "Cargar más" como fallback accesible. Los bots no hacen scroll.
// 4) Si usas Next.js, puedes colocar <Head><link rel="next" href="/productos?page=2" /></Head> cuando hasMore.
//    Ese cálculo lo sueles hacer en la página, no en el componente genérico.
